import {
  IMPLICIT_END_CALL_TOOL_DISPLAY_NAME,
  IMPLICIT_END_CALL_TOOL_ID
} from "./implicitEndCallTool.js";
import { KNOWLEDGE_RETRIEVAL_TOOL_ID } from "./knowledgeRetrievalTool.js";
import { ToolType } from "./tool.js";
import {
  TOOL_RESULT_QUERY_TOOL_DISPLAY_NAME,
  TOOL_RESULT_QUERY_TOOL_ID
} from "./tools/toolResultQuery.js";
function serializeRecordingToolActivityForAdminApi(activity) {
  return {
    id: activity.id,
    company_id: activity.company_id,
    recording_id: activity.recording_id,
    tool_id: activity.tool_id,
    created_at: activity.created_at,
    input: activity.input,
    output: activity.output
  };
}
const timelineEventTypeOrder = [
  "fragment",
  "tool_activity"
];
function buildRecordingTimelineEvents(fragments, toolActivities, recordingStartTimeMs) {
  const events = [];
  fragments.forEach((fragment, index) => {
    events.push({
      type: "fragment",
      timestamp: recordingStartTimeMs + fragment.start_time,
      data: fragment,
      index
    });
  });
  for (const activity of toolActivities) {
    events.push({
      type: "tool_activity",
      timestamp: new Date(activity.created_at).getTime(),
      data: activity
    });
  }
  events.sort((a, b) => {
    if (a.timestamp !== b.timestamp) {
      return a.timestamp - b.timestamp;
    }
    return timelineEventTypeOrder.indexOf(a.type) - timelineEventTypeOrder.indexOf(b.type);
  });
  return events;
}
function buildRecordingToolFragment(recordingId, time, activity, tool) {
  const toolId = activity.tool_id;
  if (toolId === KNOWLEDGE_RETRIEVAL_TOOL_ID) {
    return {
      recording_id: recordingId,
      time,
      tool_id: toolId,
      tool_name: "Retrieve knowledge base document",
      effect_text: `Retrieved the document titled: ${getKnowledgeRetrievalToolDocumentTitle(activity) || "unknown"}`
    };
  }
  if (toolId === IMPLICIT_END_CALL_TOOL_ID) {
    return {
      recording_id: recordingId,
      time,
      tool_id: toolId,
      tool_name: IMPLICIT_END_CALL_TOOL_DISPLAY_NAME,
      // The activity is recorded before the call is hung up, so an activity
      // that never got its response back is a hangup that failed.
      effect_text: activity.output.is_end_of_call ? "The agent hung up the call" : "The agent tried to hang up the call, but it did not go through"
    };
  }
  if (toolId === TOOL_RESULT_QUERY_TOOL_ID) {
    return {
      recording_id: recordingId,
      time,
      tool_id: toolId,
      tool_name: TOOL_RESULT_QUERY_TOOL_DISPLAY_NAME,
      input_text: formatCustomToolInput(activity.input),
      output_text: formatCustomToolOutput(activity.output)
    };
  }
  switch (tool?.type) {
    case ToolType.CALL_TRANSFER:
      return {
        recording_id: recordingId,
        time,
        tool_id: toolId,
        tool_name: tool.name,
        effect_text: `Transferred the call to: ${getCallTransferToolDestination(activity) || "unknown"}`
      };
    case ToolType.CALL_END:
      return {
        recording_id: recordingId,
        time,
        tool_id: toolId,
        tool_name: tool.name,
        effect_text: "The call ended or was transferred back to the original phone provider or IVR system"
      };
    case ToolType.CUSTOM:
    case ToolType.CODE:
    case ToolType.WEB_DOCUMENT_RESEARCH:
      return {
        recording_id: recordingId,
        time,
        tool_id: toolId,
        tool_name: tool.name,
        input_text: formatCustomToolInput(activity.input),
        output_text: formatCustomToolOutput(activity.output)
      };
    default:
      return {
        recording_id: recordingId,
        time,
        tool_id: toolId,
        tool_name: "Unknown tool"
      };
  }
}
function getKnowledgeRetrievalToolDocumentTitle(activity) {
  return activity.output.metadata?.document_title;
}
function getCallTransferToolDestination(activity) {
  return activity.output.metadata?.destination;
}
const CUSTOM_TOOL_INPUT_OUTPUT_MAX_LENGTH = 1e4;
function formatCustomToolInput(input) {
  const args = JSON.stringify(input.tool_arguments);
  if (args.length > CUSTOM_TOOL_INPUT_OUTPUT_MAX_LENGTH) {
    return "(Input too large to include.)";
  }
  return args;
}
function formatCustomToolOutput(output) {
  if (output.content === void 0 || output.content.trim().length === 0) {
    return "(No output.)";
  }
  if (output.content.length > CUSTOM_TOOL_INPUT_OUTPUT_MAX_LENGTH) {
    return "(Output too large to include.)";
  }
  return output.content;
}
export {
  buildRecordingTimelineEvents,
  buildRecordingToolFragment,
  getCallTransferToolDestination,
  getKnowledgeRetrievalToolDocumentTitle,
  serializeRecordingToolActivityForAdminApi
};
//# sourceMappingURL=recordingToolActivity.js.map
